import { useState, useEffect } from 'react';
import { View, Text, Image, FlatList, TouchableOpacity, StyleSheet, Modal, TextInput, Alert, ActivityIndicator, KeyboardAvoidingView, Platform, ScrollView } from 'react-native';
import { useLocalSearchParams, useNavigation } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useWorkoutPlans } from '@/hooks/useWorkoutPlans';
import { useExercises } from '@/hooks/useExercises';
import { ExerciseCard } from '@/components/ExerciseCard';
import { FullscreenVideoModal } from '@/components/VideoPlayer';
import { supabase } from '@/lib/supabase';
import { Exercise, PlanExercise } from '@/lib/types';

export default function PlanDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const navigation = useNavigation();
  const { planExercises, loading, addExerciseToPlan, removeExerciseFromPlan } = useWorkoutPlans({ planId: id });

  useEffect(() => {
    supabase.from('workout_plans').select('name').eq('id', id).single()
      .then(({ data }) => { if (data) navigation.setOptions({ title: data.name }); });
  }, [id]);
  const [showLibrary, setShowLibrary] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState<Exercise | null>(null);
  const [videoExercise, setVideoExercise] = useState<Exercise | null>(null);
  const [sets, setSets] = useState('3');
  const [reps, setReps] = useState('12');
  const [load, setLoad] = useState('');
  const { exercises } = useExercises();

  async function handleAddExercise() {
    if (!selectedExercise) return;
    try {
      await addExerciseToPlan(id, selectedExercise.id, parseInt(sets), parseInt(reps), load ? parseFloat(load) : null, planExercises.length);
      setSelectedExercise(null);
      setShowLibrary(false);
    } catch (e: unknown) {
      Alert.alert('Erro', e instanceof Error ? e.message : 'Tente novamente');
    }
  }

  if (loading) return <ActivityIndicator style={{ flex: 1 }} color="#C8102E" />;

  return (
    <View style={styles.container}>
      <FlatList
        data={planExercises}
        keyExtractor={pe => pe.id}
        renderItem={({ item }) => {
          const ex = item.exercise;
          const hasVideo = !!ex?.video_url;
          return (
            <View style={styles.exerciseRow}>
              {hasVideo ? (
                <TouchableOpacity onPress={() => setVideoExercise(ex!)} style={styles.thumbWrap} activeOpacity={0.8}>
                  {ex?.thumbnail_url ? (
                    <Image source={{ uri: ex.thumbnail_url }} style={styles.thumb} resizeMode="cover" />
                  ) : (
                    <View style={[styles.thumb, styles.thumbPlaceholder]} />
                  )}
                  <View style={styles.thumbPlayBadge}>
                    <Ionicons name="play" size={13} color="#fff" />
                  </View>
                </TouchableOpacity>
              ) : (
                <View style={[styles.thumbWrap, styles.thumbPlaceholder]}>
                  <Ionicons name="barbell-outline" size={20} color="#BBBBBB" />
                </View>
              )}
              <View style={styles.exerciseInfo}>
                <Text style={styles.exerciseName}>{ex?.name}</Text>
                <Text style={styles.exerciseMeta}>{item.sets}x{item.reps}{item.suggested_load ? ` • ${item.suggested_load}kg` : ''}</Text>
              </View>
              <TouchableOpacity onPress={() => removeExerciseFromPlan(item.id)}>
                <Text style={styles.remove}>✕</Text>
              </TouchableOpacity>
            </View>
          );
        }}
        contentContainerStyle={{ padding: 16 }}
        ListEmptyComponent={<Text style={styles.empty}>Nenhum exercício ainda. Adicione abaixo.</Text>}
      />
      <TouchableOpacity style={styles.fab} onPress={() => setShowLibrary(true)}>
        <Text style={styles.fabText}>+ Exercício</Text>
      </TouchableOpacity>

      <Modal visible={showLibrary} animationType="slide">
        <View style={{ flex: 1 }}>
          {selectedExercise ? (
            <KeyboardAvoidingView style={{ flex: 1 }} behavior="padding">
              <ScrollView contentContainerStyle={styles.addForm} keyboardShouldPersistTaps="handled">
                <Text style={styles.addTitle}>{selectedExercise.name}</Text>
                <Text style={styles.label}>Séries</Text>
                <TextInput style={styles.input} value={sets} onChangeText={setSets} keyboardType="numeric" />
                <Text style={styles.label}>Repetições</Text>
                <TextInput style={styles.input} value={reps} onChangeText={setReps} keyboardType="numeric" />
                <Text style={styles.label}>Carga sugerida (kg)</Text>
                <TextInput style={styles.input} value={load} onChangeText={setLoad} keyboardType="decimal-pad" placeholder="Opcional" />
                <TouchableOpacity style={styles.button} onPress={handleAddExercise}>
                  <Text style={styles.buttonText}>Adicionar à Ficha</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={() => setSelectedExercise(null)} style={{ marginTop: 12, alignItems: 'center' }}>
                  <Text style={{ color: '#888888' }}>Voltar</Text>
                </TouchableOpacity>
              </ScrollView>
            </KeyboardAvoidingView>
          ) : (
            <FlatList
              data={exercises}
              keyExtractor={e => e.id}
              renderItem={({ item }) => (
                <ExerciseCard exercise={item} onPress={setSelectedExercise} actionLabel="Selecionar" />
              )}
              contentContainerStyle={{ padding: 16 }}
              ListHeaderComponent={
                <TouchableOpacity onPress={() => setShowLibrary(false)} style={{ marginBottom: 12 }}>
                  <Text style={{ color: '#C8102E' }}>← Fechar</Text>
                </TouchableOpacity>
              }
            />
          )}
        </View>
      </Modal>

      <FullscreenVideoModal
        uri={videoExercise?.video_url}
        visible={!!videoExercise}
        onClose={() => setVideoExercise(null)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F5F5F5' },
  exerciseRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', padding: 14, borderRadius: 10, marginBottom: 8 },
  exerciseInfo: { flex: 1 },
  exerciseName: { fontSize: 15, fontWeight: '600', color: '#1A1A1A' },
  exerciseMeta: { fontSize: 13, color: '#888888', marginTop: 2 },
  thumbWrap: { width: 52, height: 52, borderRadius: 8, marginRight: 12, overflow: 'hidden', backgroundColor: '#000', alignItems: 'center', justifyContent: 'center' },
  thumb: { width: 52, height: 52 },
  thumbPlaceholder: { backgroundColor: '#ECECEC' },
  thumbPlayBadge: { position: 'absolute', right: 3, bottom: 3, width: 20, height: 20, borderRadius: 10, backgroundColor: 'rgba(200,16,46,0.92)', alignItems: 'center', justifyContent: 'center', paddingLeft: 1 },
  remove: { fontSize: 18, color: '#ff4444', paddingHorizontal: 8 },
  empty: { textAlign: 'center', color: '#aaa', marginTop: 40 },
  fab: { position: 'absolute', bottom: 24, right: 24, backgroundColor: '#C8102E', paddingHorizontal: 20, paddingVertical: 14, borderRadius: 28, elevation: 4 },
  fabText: { color: '#fff', fontWeight: 'bold', fontSize: 15 },
  addForm: { padding: 24 },
  addTitle: { fontSize: 20, fontWeight: 'bold', color: '#1A1A1A', marginBottom: 16 },
  label: { fontSize: 13, fontWeight: '600', color: '#555', marginBottom: 4, marginTop: 12 },
  input: { borderWidth: 1, borderColor: '#ddd', borderRadius: 8, padding: 12, fontSize: 15, color: '#1A1A1A' },
  button: { backgroundColor: '#C8102E', padding: 16, borderRadius: 8, alignItems: 'center', marginTop: 20 },
  buttonText: { color: '#fff', fontWeight: 'bold', fontSize: 16 },
});
